import { type JSX } from 'react';

import { CircleIndicator } from 'Components/Indicator/CircleIndicator';
import { ColumnConfig, ColumnType } from 'Components/Table/Columns';
import { Table } from 'Components/Table/Table';
import { Text } from 'Components/Text/Text';
import { Effectiveness, getEffectivenessVariantColor, numberAsEffectiveness } from 'Models/OperationalControls';
import { RiskControlMapping, WARNING_MESSAGE_INCOMPLETE_CONTROL_DISTRIBUTION, formatAverageWeightedEffectiveness } from 'Models/RiskRegister';

import styles from './MitigatingControlsListing.module.css';

export interface MitigatingControlsListingProps {
    averageEffectiveness: number;
    riskControlMapping: RiskControlMapping[];
}

export const MitigatingControlsListing = (props: MitigatingControlsListingProps): JSX.Element => {
    const controlEnvironmentTotalPercentage = props.riskControlMapping.map((mapping) => mapping.weight).reduce((acc, val) => (acc += val), 0);
    const controlsDistributedIncompletely = props.riskControlMapping.length > 0 && controlEnvironmentTotalPercentage !== 100;

    const columns: ColumnConfig<RiskControlMapping>[] = [
        {
            type: ColumnType.CONTROL,
            name: 'Control',
            getControl: (mapping) => mapping.control,
        },
        {
            type: ColumnType.DECIMAL,
            name: 'Distribution',
            getNumber: (mapping) => mapping.weight,
            numberOfDigitsAfterDecimalPoint: 0,
            decorateFormattedNumber: (formattedNumber: string) => `${formattedNumber}%`,
        },
        {
            type: ColumnType.CONTROL_EFFECTIVENESS,
            name: 'Effectiveness',
            getControlEffectiveness: (mapping) => numberAsEffectiveness(mapping.control.effectiveness.control_effectiveness) as Effectiveness,
        },
        {
            type: ColumnType.DATE,
            name: 'Last Review Date',
            getDateString: (mapping) => mapping.control.effectiveness.last_updated,
        },
    ];

    return (
        <>
            <div className={styles.controlMitigationTab}>
                <div className={styles.header}>
                    <Text variant="Header2" noStyles>
                        Controls Mitigating Risk
                    </Text>
                </div>
                <hr />
                <div className={styles.averageWeightedEffectiveness}>
                    <Text noStyles color="darkGray" variant="Header4">
                        Average Weighted Effectiveness
                    </Text>
                    <div className={styles.average}>
                        <CircleIndicator variant={getEffectivenessVariantColor(numberAsEffectiveness(props.averageEffectiveness))} />
                        <Text noStyles variant="Text1" color="blue">
                            {formatAverageWeightedEffectiveness(props.averageEffectiveness)}
                        </Text>
                    </div>
                    {controlsDistributedIncompletely && <Text color="red">{WARNING_MESSAGE_INCOMPLETE_CONTROL_DISTRIBUTION}</Text>}
                </div>
                <Table<RiskControlMapping> columns={columns} defaultSortColumn="Control" filters={[]} getUniqueIdForRow={(mapping) => mapping.control.identifier} rowsData={props.riskControlMapping} />
            </div>
        </>
    );
};
